import axios from 'axios';
import type { ValidationResult, ApiKey, User, Stats, BatchValidationResult, HistoryEntry, Webhook, WebhookDelivery } from '../types';

const API_BASE_URL = '/api';

// Создаём экземпляр axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Добавляем токен авторизации к запросам
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Обработка ошибок авторизации
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const emailValidatorService = {
  // Единичная валидация
  validateEmail: async (email: string): Promise<ValidationResult> => {
    const response = await api.post<ValidationResult>('/validate', { email });
    return response.data;
  },

  // Массовая валидация
  validateBatch: async (emails: string[]): Promise<BatchValidationResult> => {
    const response = await api.post<BatchValidationResult>('/validate/batch', { emails });
    return response.data;
  },

  // Потоковая массовая валидация через SSE
  validateBatchStream: (
    emails: string[],
    options: { checkSmtp?: boolean; checkDisposable?: boolean; useCache?: boolean },
    callbacks: {
      onResult: (result: ValidationResult & { index: number; total: number }) => void;
      onProgress: (current: number, total: number) => void;
      onDone: (summary: { total: number; valid: number; invalid: number; duration: number }) => void;
      onError: (message: string) => void;
    }
  ): (() => void) => {
    const token = localStorage.getItem('auth_token');
    const abortController = new AbortController();

    fetch('/api/validate/batch/stream', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ emails, ...options }),
      signal: abortController.signal,
    }).then(async (res) => {
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        callbacks.onError(body.error || `HTTP ${res.status}`);
        return;
      }
      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const jsonStr = trimmed.slice(5).trim();
          if (!jsonStr) continue;
          try {
            const event = JSON.parse(jsonStr);
            if (event.type === 'result') {
              callbacks.onResult(event);
            } else if (event.type === 'progress') {
              callbacks.onProgress(event.current, event.total);
            } else if (event.type === 'done') {
              callbacks.onDone({ total: event.total, valid: event.valid, invalid: event.invalid, duration: event.duration });
            } else if (event.type === 'error') {
              callbacks.onError(event.message);
            }
          } catch {
            // игнорируем некорректный JSON
          }
        }
      }
    }).catch((err) => {
      if (err.name !== 'AbortError') {
        callbacks.onError(err.message || 'Ошибка соединения');
      }
    });

    // Возвращаем функцию отмены
    return () => abortController.abort();
  },

  // Проверка статуса
  getHealth: async () => {
    const response = await api.get('/health');
    return response.data;
  },
};

export const authService = {
  // Регистрация
  register: async (email: string, password: string, name: string) => {
    const response = await api.post<{ token: string; user: User }>('/auth/register', {
      email,
      password,
      name,
    });
    return response.data;
  },

  // Логин
  login: async (email: string, password: string) => {
    const response = await api.post<{ token: string; user: User }>('/auth/login', {
      email,
      password,
    });
    return response.data;
  },

  // Выход
  logout: () => {
    localStorage.removeItem('auth_token');
  },

  // Настройка 2FA — генерация QR-кода и secret
  setup2FA: async (): Promise<{ qrCode: string; secret: string; backupCodes: string[] }> => {
    const response = await api.post<{ qrCode: string; secret: string; backupCodes: string[] }>('/auth/2fa/setup');
    return response.data;
  },

  // Верификация и включение 2FA
  verify2FA: async (token: string, backupCodes?: string[]): Promise<{ message: string; backupCodes: string[] }> => {
    const response = await api.post<{ message: string; backupCodes: string[] }>('/auth/2fa/verify', { token, backupCodes });
    return response.data;
  },

  // Отключение 2FA
  disable2FA: async (token: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/auth/2fa/disable', { token });
    return response.data;
  },

  // Удаление аккаунта (152-ФЗ): стирание/обезличивание ПДн
  deleteAccount: async (password: string, token?: string): Promise<{ success: boolean }> => {
    const response = await api.delete<{ success: boolean }>('/auth/account', { data: { password, token } });
    return response.data;
  },

  // Второй шаг входа с 2FA
  loginWith2FA: async (tempToken: string, code: string): Promise<{ token: string; user: User; usedBackupCode?: boolean }> => {
    const response = await api.post<{ token: string; user: User; usedBackupCode?: boolean }>('/auth/2fa/login', { tempToken, code });
    return response.data;
  },

  // Получение текущего пользователя
  getCurrentUser: async (): Promise<User> => {
    const response = await api.get<User>('/auth/me');
    return response.data;
  },

  // Обновление имени профиля
  updateProfile: async (name: string): Promise<User> => {
    const response = await api.put<User>('/auth/profile', { name });
    return response.data;
  },

  // Смена email
  changeEmail: async (newEmail: string, password: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/auth/change-email', { newEmail, password });
    return response.data;
  },

  // Смена пароля
  changePassword: async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/auth/change-password', { currentPassword, newPassword });
    return response.data;
  },

  // Верификация email по токену
  verifyEmail: async (token: string): Promise<{ message: string }> => {
    const response = await api.get<{ message: string }>(`/auth/verify-email?token=${token}`);
    return response.data;
  },

  // Повторная отправка письма верификации
  resendVerification: async (): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/auth/resend-verification');
    return response.data;
  },

  // Журнал действий пользователя
  getAuditLog: async (): Promise<AuditLogEntry[]> => {
    const response = await api.get<AuditLogEntry[]>('/auth/audit-log');
    return response.data;
  },

  // Настройки email-уведомлений
  getNotificationPrefs: async (): Promise<NotificationPrefs> => {
    const response = await api.get<NotificationPrefs>('/auth/notification-prefs');
    return response.data;
  },

  updateNotificationPrefs: async (prefs: Partial<NotificationPrefs>): Promise<NotificationPrefs> => {
    const response = await api.put<NotificationPrefs>('/auth/notification-prefs', prefs);
    return response.data;
  },

  // Реферальная программа
  getReferral: async (): Promise<ReferralInfo> => {
    const response = await api.get<ReferralInfo>('/auth/referral');
    return response.data;
  },

  applyReferral: async (code: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/auth/referral/apply', { referralCode: code });
    return response.data;
  },

  updateLocale: (locale: string) => api.patch('/auth/locale', { locale }).then(r => r.data),
};

export const apiKeyService = {
  getKeys: async (): Promise<ApiKey[]> => {
    const response = await api.get<ApiKey[]>('/keys');
    return response.data;
  },

  createKey: async (name: string): Promise<ApiKey> => {
    const response = await api.post<ApiKey>('/keys', { name });
    return response.data;
  },

  deleteKey: async (id: string): Promise<void> => {
    await api.delete(`/keys/${id}`);
  },

  deactivateKey: async (id: string): Promise<ApiKey> => {
    const response = await api.put<ApiKey>(`/keys/${id}`, { active: false });
    return response.data;
  },

  rotateKey: async (id: string): Promise<{ oldKeyId: string; newKey: ApiKey }> => {
    const response = await api.post<{ oldKeyId: string; newKey: ApiKey }>(`/keys/${id}/rotate`);
    return response.data;
  },

  updateIpWhitelist: async (id: string, ips: string[]): Promise<ApiKey> => {
    const response = await api.put<ApiKey>(`/keys/${id}/ip-whitelist`, { ips });
    return response.data;
  },

  updateScopes: async (id: string, scopes: string[]): Promise<ApiKey> => {
    const response = await api.put<ApiKey>(`/keys/${id}/scopes`, { scopes });
    return response.data;
  },
};

export const statsService = {
  // Получить статистику
  getStats: async (): Promise<Stats> => {
    const response = await api.get<Stats>('/stats');
    return response.data;
  },

  // Получить детальную статистику по периодам
  getStatsByPeriod: async (days: number): Promise<Stats> => {
    const response = await api.get<Stats>(`/stats?days=${days}`);
    return response.data;
  },
};

export const deliverabilityService = {
  // Public: run one-time domain check
  checkDomain: async (domain: string) => {
    const response = await api.post('/deliverability/check', { domain });
    return response.data;
  },

  // Auth: save domain and run initial check
  saveDomain: async (domain: string) => {
    const response = await api.post('/deliverability/domains', { domain });
    return response.data;
  },

  // Auth: list saved domains
  getDomains: async () => {
    const response = await api.get('/deliverability/domains');
    return response.data;
  },

  // Auth: get domain with latest report
  getDomain: async (id: number) => {
    const response = await api.get(`/deliverability/domains/${id}`);
    return response.data;
  },

  // Auth: re-run check for saved domain
  recheckDomain: async (id: number) => {
    const response = await api.post(`/deliverability/domains/${id}/recheck`);
    return response.data;
  },

  // Auth: get check history
  getHistory: async (id: number) => {
    const response = await api.get(`/deliverability/domains/${id}/history`);
    return response.data;
  },

  // Auth: delete saved domain
  deleteDomain: async (id: number) => {
    await api.delete(`/deliverability/domains/${id}`);
  },

  // Auth: get alert preferences for a domain (Basic+ plan)
  getAlertPrefs: async (domainId: number) => {
    const response = await api.get(`/deliverability/domains/${domainId}/alerts`);
    return response.data;
  },

  // Auth: save alert preferences for a domain (Basic+ plan)
  saveAlertPrefs: async (domainId: number, prefs: {
    email_alerts?: boolean;
    telegram_alerts?: boolean;
    telegram_chat_id?: string;
    weekly_digest?: boolean;
    alert_on_critical?: boolean;
    alert_on_policy_change?: boolean;
    alert_on_score_drop?: boolean;
  }) => {
    const response = await api.put(`/deliverability/domains/${domainId}/alerts`, prefs);
    return response.data;
  },

  // Auth: import domains from a CSV file
  importCsv: async (file: File): Promise<{ imported: number; skipped: number; errors: { value: string; reason: string }[] }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/deliverability/domains/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export const historyService = {
  getHistory: async (apiKey: string, limit = 50, offset = 0) => {
    const response = await api.get('/history', {
      headers: { 'X-API-Key': apiKey },
      params: { limit, offset },
    });
    return response.data as {
      data: HistoryEntry[];
      pagination: { total: number; limit: number; offset: number; hasMore: boolean };
    };
  },

  exportCsv: async (apiKey: string, opts: { from?: string; to?: string; limit?: number } = {}): Promise<Blob> => {
    const token = localStorage.getItem('auth_token');
    const params = new URLSearchParams();
    if (opts.from)  params.set('from',  opts.from);
    if (opts.to)    params.set('to',    opts.to);
    if (opts.limit) params.set('limit', String(opts.limit));
    const url = `/api/history/export${params.toString() ? '?' + params.toString() : ''}`;
    const res = await fetch(url, {
      headers: {
        'X-API-Key': apiKey,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(body || `HTTP ${res.status}`);
    }
    return res.blob();
  },
};

const webhookHeaders = (apiKey: string) => {
  const token = localStorage.getItem('auth_token');
  return {
    'X-API-Key': apiKey,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export const webhookService = {
  getWebhooks: async (apiKey: string): Promise<Webhook[]> => {
    const res = await axios.get<{ webhooks: Webhook[] }>('/api/webhooks', {
      headers: webhookHeaders(apiKey),
    });
    return res.data.webhooks;
  },

  createWebhook: async (apiKey: string, url: string, events: string[]): Promise<Webhook> => {
    const res = await axios.post<{ webhook: Webhook }>(
      '/api/webhooks',
      { url, events },
      { headers: webhookHeaders(apiKey) },
    );
    return res.data.webhook;
  },

  updateWebhook: async (
    apiKey: string,
    id: number,
    data: Partial<{ active: boolean; url: string; events: string[] }>,
  ): Promise<Webhook> => {
    const res = await axios.put<{ webhook: Webhook }>(
      `/api/webhooks/${id}`,
      data,
      { headers: webhookHeaders(apiKey) },
    );
    return res.data.webhook;
  },

  deleteWebhook: async (apiKey: string, id: number): Promise<void> => {
    await axios.delete(`/api/webhooks/${id}`, { headers: webhookHeaders(apiKey) });
  },

  testWebhook: async (apiKey: string, url: string): Promise<void> => {
    await axios.post('/api/webhooks/test', { url }, { headers: webhookHeaders(apiKey) });
  },

  getDeliveries: async (id: number): Promise<WebhookDelivery[]> => {
    const token = localStorage.getItem('auth_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await axios.get<{ deliveries: WebhookDelivery[] }>(
      `/api/webhooks/${id}/deliveries`,
      { headers },
    );
    return res.data.deliveries;
  },
};

export const adminService = {
  getUsers: async (params: {
    page?: number;
    limit?: number;
    search?: string;
    token: string;
  }): Promise<{ users: AdminUser[]; total: number; page: number; limit: number }> => {
    const { token, ...queryParams } = params;
    const response = await axios.get('/api/admin/users', {
      headers: { Authorization: `Bearer ${token}` },
      params: queryParams,
    });
    return response.data;
  },

  updateUserPlan: async (id: number, planId: string, token: string): Promise<{ user: AdminUser }> => {
    const response = await axios.patch(`/api/admin/users/${id}/plan`, { planId }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  toggleUserActive: async (id: number, active: boolean, token: string): Promise<{ user: AdminUser }> => {
    const response = await axios.patch(`/api/admin/users/${id}/active`, { active }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  getBlacklist: (page: number = 1, limit: number = 50, token: string): Promise<{domains: Array<{id: number; domain: string; reason: string | null; created_at: string}>; total: number; page: number; limit: number}> =>
    axios.get(`/api/admin/domain-blacklist?page=${page}&limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.data),

  addBlacklistDomain: (domain: string, reason: string | undefined, token: string) =>
    axios.post('/api/admin/domain-blacklist', { domain, reason }, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.data),

  bulkImportBlacklist: (domains: string[], token: string) =>
    axios.post('/api/admin/domain-blacklist/bulk', { domains }, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.data),

  deleteBlacklistDomain: (id: number, token: string) =>
    axios.delete(`/api/admin/domain-blacklist/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.data),
};

export interface ReferralInfo {
  referralCode: string | null;
  referralLink: string | null;
  totalReferrals: number;
  bonusRequests: number;
}

export interface NotificationPrefs {
  quotaAlerts: boolean;
  deliverabilityAlerts: boolean;
  weeklyDigest: boolean;
  inactiveKey: boolean;
  marketing: boolean;
}

export interface AuditLogEntry {
  id: number;
  action: string;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
}

export interface AdminUser {
  id: number;
  email: string;
  name: string;
  plan_id: string;
  requests_used: number;
  requests_limit: number;
  email_verified: boolean;
  is_active: boolean;
  created_at: string;
  api_keys_count: string;
  domains_count: string;
}

export interface EmailList {
  id: number;
  name: string;
  email_count?: number;
  emails?: string[];
  last_validated_at: string | null;
  created_at: string;
  updated_at?: string;
}

export interface EmailListValidationResult {
  email: string;
  valid: boolean;
  score: number;
  risk: string;
  domain: string;
  mxRecords: boolean;
  disposable: boolean;
  roleAccount: boolean;
  cached: boolean;
  reason?: string;
}

export interface EmailListValidationResponse {
  total: number;
  valid: number;
  invalid: number;
  results: EmailListValidationResult[];
}

export const emailListsService = {
  // Получить все списки пользователя
  getLists: async (): Promise<EmailList[]> => {
    const response = await api.get<EmailList[]>('/email-lists');
    return response.data;
  },

  // Создать новый список
  createList: async (name: string, emails: string[]): Promise<EmailList> => {
    const response = await api.post<EmailList>('/email-lists', { name, emails });
    return response.data;
  },

  // Получить список с emails
  getList: async (id: number): Promise<EmailList> => {
    const response = await api.get<EmailList>(`/email-lists/${id}`);
    return response.data;
  },

  // Удалить список
  deleteList: async (id: number): Promise<void> => {
    await api.delete(`/email-lists/${id}`);
  },

  // Обновить список (имя и/или emails)
  updateList: async (id: number, data: { name?: string; emails?: string[] }): Promise<EmailList> => {
    const response = await api.put<EmailList>(`/email-lists/${id}`, data);
    return response.data;
  },

  // Запустить валидацию списка
  validateList: async (id: number): Promise<EmailListValidationResponse> => {
    const response = await api.post<EmailListValidationResponse>(`/email-lists/${id}/validate`);
    return response.data;
  },
};

export const blacklistService = {
  getList: (): Promise<Array<{id: number; domain: string; scope: string; reason: string | null; created_at: string}>> =>
    api.get('/domain-blacklist').then(r => r.data),
  addDomain: (domain: string, reason?: string) =>
    api.post('/domain-blacklist', { domain, reason }).then(r => r.data),
  deleteDomain: (id: number) =>
    api.delete(`/domain-blacklist/${id}`).then(r => r.data),
};

export default api;
